import { prefersReducedMotion } from '@/lib/motion';

// BentoWipe — the flourish when a tool card is picked (2026-09-24). A disc in
// the card's accent opens from the tap point to cover the screen, the stop
// switches underneath it, and the cover dissolves into the new stop.
//
// Cover first, switch second (round 2 critique, 2026-09-24). The first
// version switched the stop in the click handler and opened the disc after:
// rendering a stop blocks the page for 190-630 ms, so no frame came between
// the tap and the swap — neither the press nor the disc ever showed, and the
// disc then grew over a page that had already changed (the transition ran
// backwards). Now the disc opens on an idle page, `onCovered` runs once it
// fills the screen, and the cover holds until the caller says the new stop
// is in (`release`), or a ceiling passes. A stop that takes long to render
// just holds the cover a little longer; nothing half-built ever shows.
//
// Compositor only (round 3 critique, 2026-09-25). The disc used to be a
// full-screen layer whose clip-path circle grew, which the main thread has
// to repaint every frame: on a mid-range Android at 4x CPU the disc showed
// 0.3-1.25 s after the tap and stuttered. Now it is a round layer of the
// final size, centred on the tap point, that scales up from nothing; the
// tool's name rises in on the cover; the dissolve is an opacity fade. All
// three are started in the tap's own frame and run on the compositor, so
// they keep moving however long React then takes to build the stop.
//
// The hold is a title card, not a blank screen (round 3). Building a stop
// takes 0.6-1 s on a laptop and seconds on a throttled phone, and a flat
// teal or green screen for that long read as a loading screen. The cover
// now carries the name of the tool being opened (its studio tag above it),
// the same words the compass shows once the stop is in.
//
// A settled page (round 3). After `release` the cover waits for the frame
// that paints the new stop, then for two frames without scrolling, before
// it fades: it used to lift while the page's smooth scroll was still
// carrying the new stop ~600 px up the screen (HeroSection now makes that
// scroll instant under the cover). Frames only come when the main thread is
// free, so this also keeps the fade from opening onto a page that is still
// being built. `release` itself now comes from the commit that swaps the
// stop in, not from after its effects.
//
// It is deliberately NOT part of the React tree: picking a card unmounts the
// chooser that started it, so the layer is a plain fixed node on <body> that
// removes itself. It never takes a pointer event, so nothing is blocked.
// While it is up it carries [data-bento-wipe] and the window hears
// `bentowipe:start` / `bentowipe:end`, so costly work behind it (the hero's
// WebGL loop) can stand down for the second or so nobody can see it.
//
// Returns null — and does nothing — for reduced motion and wherever the Web
// Animations API is missing (jsdom in tests, very old browsers): the caller
// then switches at once.

const OPEN_MS = 340;
const DISSOLVE_MS = 420;
/** Longest the cover waits for `release` before it dissolves anyway. A
 *  throttled phone takes 2-4 s to build a stop; lifting before it is in
 *  would show the old cards again. */
const HOLD_MAX_MS = 4000;
/** After `release`, longest it waits for the page to stop scrolling. */
const SETTLE_MAX_MS = 700;
/** Beyond the farthest corner: room for a phone's address bar to slide
 *  away, which makes the viewport taller while the cover is up. */
const EDGE_PX = 96;
const EXPO_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';

export const BENTO_WIPE_START = 'bentowipe:start';
export const BENTO_WIPE_END = 'bentowipe:end';

export interface BentoWipe {
  /** The new stop is in the DOM: dissolve the cover once it has painted. */
  release(): void;
}

export interface BentoWipeOptions {
  /** Runs once the disc covers the screen: switch the stop here. */
  onCovered: () => void;
  onEnd?: () => void;
  /** The tool being opened, shown on the cover while the stop is built. */
  title?: { name: string; tag?: string };
  /** Picked with a mouse. The cursor ring only re-reads what is under the
   *  pointer when the pointer moves, so once the page has changed beneath a
   *  still mouse it would keep the old card's word; the wipe nudges it. */
  mouse?: boolean;
}

/** Tells pointer followers (the cursor ring) that `target` is under the
 *  pointer now. */
function aimPointerAt(target: Element, x: number, y: number) {
  if (typeof PointerEvent !== 'function') return;
  try {
    target.dispatchEvent(new PointerEvent('pointermove', {
      bubbles: true, composed: true, clientX: x, clientY: y, pointerType: 'mouse', isPrimary: true,
    }));
  } catch { /* cosmetic only */ }
}

/** Re-aims pointer followers at whatever is under the pointer now. */
function reaimPointer(x: number, y: number) {
  if (typeof document.elementFromPoint !== 'function') return;
  const under = document.elementFromPoint(x, y);
  if (under) aimPointerAt(under, x, y);
}

/** The title card: the studio tag, in a light accent, over the tool's name. Plain
 *  text nodes (textContent), so a translation can never inject markup. */
function buildTitle(rgb: string, { name, tag }: { name: string; tag?: string }): HTMLElement {
  const box = document.createElement('div');
  Object.assign(box.style, {
    position: 'absolute',
    inset: '0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
    opacity: '0',
  } as Partial<CSSStyleDeclaration>);
  if (tag) {
    const t = document.createElement('p');
    t.textContent = tag;
    Object.assign(t.style, {
      margin: '0 0 14px',
      fontFamily: '"DM Sans", system-ui, sans-serif',
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: 'rgb(255 255 255 / 0.8)',
    } as Partial<CSSStyleDeclaration>);
    // The accent, lifted towards white: the pure accent sat on its own glow
    // and all but vanished (coral on coral). Browsers without color-mix
    // ignore it and keep the white above.
    t.style.color = `color-mix(in oklab, rgb(${rgb}) 50%, white)`;
    box.appendChild(t);
  }
  const n = document.createElement('p');
  n.textContent = name;
  Object.assign(n.style, {
    margin: '0',
    maxWidth: '14ch',
    fontFamily: 'Syne, system-ui, sans-serif',
    fontSize: 'clamp(2rem, 7vw, 4.25rem)',
    fontWeight: '700',
    lineHeight: '1.04',
    letterSpacing: '-0.025em',
    color: 'hsl(210 15% 97%)',
    textWrap: 'balance',
    textShadow: `0 0 48px rgb(${rgb} / 0.35)`,
  } as Partial<CSSStyleDeclaration>);
  box.appendChild(n);
  return box;
}

export function playBentoWipe(
  origin: { x: number; y: number },
  rgb: string,
  { onCovered, onEnd, title, mouse = false }: BentoWipeOptions,
): BentoWipe | null {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null;
  if (prefersReducedMotion()) return null;
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (!w || !h) return null;
  const layer = document.createElement('div');
  if (typeof layer.animate !== 'function') return null;

  const x = Math.round(Math.min(Math.max(origin.x, 0), w));
  const y = Math.round(Math.min(Math.max(origin.y, 0), h));
  // Radius that reaches the farthest corner from the tap point.
  const r = Math.ceil(Math.hypot(Math.max(x, w - x), Math.max(y, h - y))) + EDGE_PX;

  // The screen-sized layer holds the disc (clipped to the screen, so only
  // what shows is ever drawn) and the title, and is what fades out.
  layer.setAttribute('aria-hidden', 'true');
  layer.setAttribute('data-bento-wipe', '');
  Object.assign(layer.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '90',
    pointerEvents: 'none',
    overflow: 'hidden',
    contain: 'strict',
  } as Partial<CSSStyleDeclaration>);
  const disc = document.createElement('div');
  Object.assign(disc.style, {
    position: 'absolute',
    left: `${x - r}px`,
    top: `${y - r}px`,
    width: `${r * 2}px`,
    height: `${r * 2}px`,
    borderRadius: '50%',
    // Bright accent at the tap point, easing into the near-black stage, so
    // the screen reads as the card opening rather than a flash of colour.
    // The base is opaque: the swap underneath must not show through, and a
    // 3% ghost of the old cards behind the title card read as dirt.
    background: [
      `radial-gradient(closest-side, rgb(${rgb} / 0.78) 0%, rgb(${rgb} / 0.34) 22%, transparent 55%)`,
      'hsl(220 18% 5%)',
    ].join(', '),
    transform: 'scale(0.002)',
    willChange: 'transform',
  } as Partial<CSSStyleDeclaration>);
  layer.appendChild(disc);
  const card = title?.name ? buildTitle(rgb, title) : null;
  if (card) layer.appendChild(card);
  document.body.appendChild(layer);
  // The cover is not a card: the ring drops the picked card's word ("Open")
  // and shrinks back to the plain ring, instead of floating the word over the
  // title card for the whole hold (round 4 critique, 2026-09-25). The cover
  // never takes pointer events, so this one move is the only way it gets
  // aimed at; dispatchEvent ignores pointer-events.
  if (mouse) aimPointerAt(layer, x, y);

  const signal = (type: string) => {
    try { window.dispatchEvent(new Event(type)); } catch { /* listeners are optional */ }
  };
  signal(BENTO_WIPE_START);

  // The latest mouse position, for re-aiming the cursor at the new stop.
  let px = x;
  let py = y;
  const onMove = (e: PointerEvent) => {
    if (e.isTrusted && e.pointerType === 'mouse') {
      px = e.clientX;
      py = e.clientY;
    }
  };
  if (mouse) window.addEventListener('pointermove', onMove, { passive: true });

  let ended = false;
  let covered = false;
  let dissolving = false;
  let released = false;
  let ceiling = 0;
  const end = () => {
    if (ended) return;
    ended = true;
    window.clearTimeout(ceiling);
    window.clearTimeout(failsafe);
    window.removeEventListener('pointermove', onMove);
    layer.remove();
    signal(BENTO_WIPE_END);
    try { onEnd?.(); } catch { /* the caller's bookkeeping only */ }
  };
  // Belt and braces: never leave a layer behind, nor a pick unmade, if an
  // animation stalls (a backgrounded tab can pause it indefinitely).
  let failsafe = window.setTimeout(() => {
    if (!covered) {
      covered = true;
      try { onCovered(); } catch { /* the layer still goes */ }
    }
    end();
  }, OPEN_MS + HOLD_MAX_MS + SETTLE_MAX_MS + DISSOLVE_MS + 1200);

  const dissolve = () => {
    if (dissolving || ended) return;
    dissolving = true;
    window.clearTimeout(ceiling);
    // From here only the fade's own end can be late (its finish event waits
    // for a free main thread): give it its time, not the cover's.
    window.clearTimeout(failsafe);
    failsafe = window.setTimeout(end, DISSOLVE_MS + 1500);
    if (mouse) reaimPointer(px, py);
    try {
      const out = layer.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: DISSOLVE_MS, easing: 'cubic-bezier(0.33, 1, 0.68, 1)', fill: 'forwards' },
      );
      out.onfinish = end;
      out.oncancel = end;
    } catch {
      end();
    }
  };

  // Frame 1 paints the new stop under the cover; then the cover waits for
  // two frames without scrolling (a scroll still settling the new stop
  // would otherwise slide the page under the fade).
  const settleThenDissolve = () => {
    requestAnimationFrame(() => {
      if (ended || dissolving) return;
      const until = performance.now() + SETTLE_MAX_MS;
      let lastY = window.scrollY;
      let still = 0;
      const watch = () => {
        if (ended || dissolving) return;
        const sy = window.scrollY;
        still = sy === lastY ? still + 1 : 0;
        lastY = sy;
        if (still >= 2 || performance.now() >= until) dissolve();
        else requestAnimationFrame(watch);
      };
      requestAnimationFrame(watch);
    });
  };

  const cover = () => {
    if (covered || ended) return;
    covered = true;
    // The switch runs under a full cover. If it throws, the cover still
    // lifts rather than hiding the page.
    try { onCovered(); } catch { dissolve(); return; }
    // Released already (the chooser went away while the disc was opening):
    // nothing left to wait for.
    if (released) settleThenDissolve();
    else ceiling = window.setTimeout(dissolve, HOLD_MAX_MS);
  };

  try {
    // Fast out of the tap (the disc is there on the first frames), easing
    // into the far corners.
    const open = disc.animate(
      [{ transform: 'scale(0.002)' }, { transform: 'scale(1)' }],
      { duration: OPEN_MS, easing: 'cubic-bezier(0.3, 0.7, 0.25, 1)', fill: 'forwards' },
    );
    open.onfinish = cover;
    open.oncancel = cover;
    // The name rises in as the disc reaches the edges. Started now, in the
    // tap's frame, so it plays on the compositor even while the stop is
    // being built.
    card?.animate(
      [{ opacity: 0, transform: 'translate3d(0, 18px, 0)' }, { opacity: 1, transform: 'translate3d(0, 0, 0)' }],
      { duration: 620, delay: OPEN_MS * 0.55, easing: EXPO_OUT, fill: 'both' },
    );
  } catch {
    window.removeEventListener('pointermove', onMove);
    window.clearTimeout(failsafe);
    layer.remove();
    signal(BENTO_WIPE_END);
    return null;
  }

  return {
    release() {
      if (released) return;
      released = true;
      if (covered) settleThenDissolve();
    },
  };
}
